import React, { useCallback } from 'react'

import { Button, Check, Icon, Modal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import { TIER } from '../constants'

function formatCurrency(amount) {
  return `$${amount.toFixed(2)}`
}

// UTC, not the browser zone -- see the matching note in CancelSubscriptionModal/useCancelModal.js.
function formatAccessDate(isoDate, locale = 'en') {
  return new Date(isoDate).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function CancelConfirmationModal({
  isOpen,
  onDone,
  accessUntil = null,
  accessUntilLabel = null,
  cancelFee = 0,
  locale = 'en',
  tier = TIER.ESSENTIALS,
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(
      `subscription_management.cancel_modal.confirmation.${key}`,
      { ns: 'rails', lng: locale, ...options }
    ),
    [locale]
  )

  // accessUntilLabel arrives already formatted (the serialized period end); accessUntil is the
  // raw date from the fee preview. Only one of the two is ever set.
  const dateFormatted = accessUntilLabel
    || (accessUntil ? formatAccessDate(accessUntil, locale) : null)
  const isAiPlan = tier === TIER.AI_STUDIO

  let description
  if (cancelFee > 0 && dateFormatted) {
    description = translate('body_fee', {
      date: dateFormatted,
      fee: formatCurrency(cancelFee),
    })
  } else if (isAiPlan) {
    description = dateFormatted
      ? translate('body_no_fee_ai', { date: dateFormatted })
      : translate('body_no_fee_no_date_ai')
  } else if (dateFormatted) {
    description = translate('body_no_fee', { date: dateFormatted })
  } else {
    description = translate('body_no_fee_no_date')
  }

  const title = isAiPlan && dateFormatted
    ? translate('title_ai', { date: dateFormatted })
    : translate('title')

  return (
    <Modal
      isOpen={isOpen}
      onClose={onDone}
      header={translate('header')}
      width="small"
      canClose
      customClass="cancel-confirmation-modal"
    >
      <div className="cancel-confirmation-modal__body">
        <div className="cancel-confirmation-modal__icon">
          <Icon svgComponent={Check} size="large" color="dark" />
        </div>
        <h4 className="cancel-confirmation-modal__title">{title}</h4>
        <p className="cgt-text-medium cancel-confirmation-modal__description">{description}</p>
        <Button
          type="primary"
          actionType="button"
          onClick={onDone}
          customClass="cancel-confirmation-modal__done"
        >
          {translate('done_button')}
        </Button>
      </div>
    </Modal>
  )
}

CancelConfirmationModal.propTypes = {
  isOpen: bool.isRequired,
  onDone: func.isRequired,
  accessUntil: string,
  accessUntilLabel: string,
  cancelFee: number,
  locale: string,
  tier: string,
}

export default CancelConfirmationModal
